import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Home, Key } from 'lucide-react';
import { useRBAC } from '../../contexts/RBACContext';

interface AccessDeniedProps {
  requiredPermissions?: { resource: string; action: string; scope?: string }[];
  requiredRoles?: string[];
}

const formatPermission = (perm: { resource: string; action: string; scope?: string }): string => {
  const parts = [perm.resource, perm.action];
  if (perm.scope) parts.push(perm.scope);
  return parts.join(':');
};

export const AccessDenied: React.FC<AccessDeniedProps> = ({
  requiredPermissions = [],
  requiredRoles = [],
}) => {
  const navigate = useNavigate();
  const { user, permissions: userPermissions } = useRBAC();

  const missingPermissions = requiredPermissions.filter((required) => {
    return !userPermissions.some((userPerm) => {
      const resourceMatch = userPerm.resource === required.resource;
      const actionMatch = userPerm.action === required.action;
      const scopeMatch = !required.scope || userPerm.scope === required.scope || userPerm.scope === 'global';
      return resourceMatch && actionMatch && scopeMatch;
    });
  });

  return (
    <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 text-center">
        <div>
          <div className="mx-auto h-20 w-20 flex items-center justify-center rounded-full bg-red-100">
            <Shield className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">
            Access Denied
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            You don't have permission to access this resource.
          </p>
        </div>

        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-5 text-left">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Signed in as
              </h3>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">User:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {user?.name || 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Email:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {user?.email || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            {missingPermissions.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                  <Key className="h-4 w-4 text-amber-600" />
                  Missing permission{missingPermissions.length > 1 ? 's' : ''}
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {missingPermissions.map((perm, idx) => (
                    <span
                      key={idx}
                      className="font-mono text-xs bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1 rounded"
                    >
                      {formatPermission(perm)}
                    </span>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Ask an administrator to grant {missingPermissions.length > 1 ? 'these permissions' : 'this permission'} to one of your roles.
                </p>
              </div>
            )}

            {requiredRoles.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Required role{requiredRoles.length > 1 ? 's' : ''}
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {requiredRoles.map((role) => (
                    <span
                      key={role}
                      className="font-mono text-xs bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1 rounded"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => window.history.back()}
            className="flex-1 flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex-1 flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm bg-blue-600 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Home className="h-4 w-4 mr-2" />
            Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied;
